import './styles.scoped.css'
import {Fragment} from 'react'
import {Skeleton} from '@mui/material'
import {SkeletonContainer, TextSkeleton, ButtonSkeleton, AvatarSkeleton} from '../../../../components'

// Layout-matched placeholders for the dashboard's visit tabs and drill-down modals.

const serviceWidths = [140, 116, 164]
const nameWidths = [124, 100, 140, 112]
const emailWidths = [148, 128, 166, 136]

const Line = ({width, fontSize = 14}) => <TextSkeleton width={width} fontSize={fontSize} />

// The tinted "date | time" pill.
const DatetimeSkeleton = () => (
    <div className="datetime">
        <Skeleton variant="rounded" width={118} height={14} />
        <Skeleton variant="rounded" width={124} height={14} />
    </div>
)

// The stacked date / time pill used where a card shows scheduled and actual times on mobile.
const Datetime2Skeleton = ({badge}) => (
    <div className="datetime2">
        <Skeleton variant="rounded" width={118} height={14} />
        <Skeleton variant="rounded" width={124} height={14} />
        {badge && <Line width={badge} fontSize={12} />}
    </div>
)

// Caption label, 40px avatar, name and email.
const PersonSkeleton = ({index, labelWidth = 56}) => (
    <div>
        <Line width={labelWidth} fontSize={12} />
        <div className="people-card">
            <AvatarSkeleton size={40} />
            <div className="people-profile-card">
                <Line width={nameWidths[index % nameWidths.length]} fontSize={16} />
                <Line width={emailWidths[index % emailWidths.length]} fontSize={12} />
            </div>
        </div>
    </div>
)

const MobileButtonsSkeleton = ({rows}) => (
    <div className="actions-mobile">
        {rows.map((count, row) => (
            <div className="actions-row" key={row}>
                {Array.from({length: count}).map((_, index) => (
                    <ButtonSkeleton key={index} width="100%" sx={{flex: 1}} />
                ))}
            </div>
        ))}
    </div>
)

// One card per visit, mirroring the Today's Schedule Overview tabs.
// people: parties shown (1 = client only); times: 1, or 2 for scheduled + actual;
// badge: width of the status overline next to the times; buttons: desktop button widths;
// mobileButtons: full-width buttons per row on mobile.
export const VisitCardsSkeleton = ({mobile, count = 3, people = 2, times = 1, badge, buttons = [], mobileButtons = []}) => (
    <SkeletonContainer>
        {Array.from({length: count}).map((_, index) => (
            <Fragment key={index}>
                <br />
                {mobile ? (
                    <div className="card">
                        <Line width={serviceWidths[index % serviceWidths.length]} fontSize={16} />
                        <br />
                        <br />
                        {times === 2 ? (
                            <>
                                <div className="row-content">
                                    <Line width={90} fontSize={12} />
                                    <Datetime2Skeleton />
                                </div>
                                <br />
                                <div className="row-content">
                                    <Line width={90} fontSize={12} />
                                    <Datetime2Skeleton badge={badge} />
                                </div>
                            </>
                        ) : (
                            <>
                                <DatetimeSkeleton />
                                {badge && <Line width={badge} fontSize={12} />}
                            </>
                        )}
                        {Array.from({length: people}).map((_, person) => (
                            <Fragment key={person}>
                                <br />
                                <PersonSkeleton index={index + person} labelWidth={person ? 72 : 48} />
                            </Fragment>
                        ))}
                        {mobileButtons.length > 0 && (
                            <>
                                <br />
                                <MobileButtonsSkeleton rows={mobileButtons} />
                            </>
                        )}
                    </div>
                ) : (
                    <div className="card">
                        <div className="card-top">
                            <Line width={serviceWidths[index % serviceWidths.length]} fontSize={16} />
                            {times === 2 ? (
                                <div className="card-top-right-column">
                                    <div className="scheduled">
                                        <Line width={90} fontSize={12} />
                                        <DatetimeSkeleton />
                                    </div>
                                    <div className="scheduled">
                                        <Line width={90} fontSize={12} />
                                        <DatetimeSkeleton />
                                        {badge && <Line width={badge} fontSize={12} />}
                                    </div>
                                </div>
                            ) : badge ? (
                                <div className="card-top-right">
                                    <DatetimeSkeleton />
                                    <Line width={badge} fontSize={12} />
                                </div>
                            ) : (
                                <DatetimeSkeleton />
                            )}
                        </div>
                        <div className="card-bottom">
                            <div className="people-container">
                                {Array.from({length: people}).map((_, person) => (
                                    <PersonSkeleton key={person} index={index + person} labelWidth={person ? 72 : 48} />
                                ))}
                            </div>
                            <div className="actions">
                                {buttons.map((width, button) => (
                                    <ButtonSkeleton key={button} width={width} />
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </Fragment>
        ))}
    </SkeletonContainer>
)

// The conflict tab: double-booked caregiver up top, the clients they are booked with below.
export const ConflictCardsSkeleton = ({mobile, count = 2, clients = 3}) => (
    <SkeletonContainer>
        {Array.from({length: count}).map((_, index) => (
            <Fragment key={index}>
                <br />
                {mobile ? (
                    <div className="card">
                        <DatetimeSkeleton />
                        <br />
                        <div className="people-card">
                            <Line width={72} fontSize={12} />
                            <AvatarSkeleton size={40} />
                            <div className="people-profile-card">
                                <Line width={nameWidths[index % nameWidths.length]} fontSize={16} />
                                <Line width={emailWidths[index % emailWidths.length]} fontSize={12} />
                            </div>
                        </div>
                        <br />
                        <Line width={150} fontSize={16} />
                        <br />
                        {Array.from({length: clients}).map((_, client) => (
                            <Fragment key={client}>
                                <br />
                                <div className="client-card">
                                    <PersonSkeleton index={client} labelWidth={48} />
                                </div>
                            </Fragment>
                        ))}
                        <br />
                        <Line width={64} fontSize={16} />
                    </div>
                ) : (
                    <div className="card">
                        <div className="card-top">
                            <div className="card-top-left">
                                <DatetimeSkeleton />
                                <Line width={72} fontSize={12} />
                                <div className="people-card">
                                    <AvatarSkeleton size={40} />
                                    <div className="people-profile-card">
                                        <Line width={nameWidths[index % nameWidths.length]} fontSize={16} />
                                        <Line width={emailWidths[index % emailWidths.length]} fontSize={12} />
                                    </div>
                                </div>
                            </div>
                            <Line width={150} fontSize={16} />
                        </div>
                        <br />
                        <div className="card-bottom-clients">
                            {Array.from({length: clients}).map((_, client) => (
                                <div className="client-card" key={client}>
                                    <PersonSkeleton index={client} labelWidth={48} />
                                </div>
                            ))}
                            <Line width={64} fontSize={16} />
                        </div>
                    </div>
                )}
            </Fragment>
        ))}
    </SkeletonContainer>
)

// Drill-down modal card rows:
// {text: width, fontSize}  a line of text
// {person: avatarSize, nameFontSize, sub: subtitle width}  avatar + name (+ subtitle)
// {split: [left, right], fontSize}  two lines pushed apart, like a card header
// {contact: true}  email | phone with their icons
// {eligibility: true}  "Eligibility:" + status icon + last-checked date
// {buttons: [widths]}
const DrilldownRow = ({row, index}) => {
    if (row.person) {
        return (
            <div className="drilldown-row">
                <AvatarSkeleton size={row.person} />
                <div className="people-profile-card" style={{gap: row.sub ? 0 : 4}}>
                    <Line width={nameWidths[index % nameWidths.length]} fontSize={row.nameFontSize || 14} />
                    {row.sub && <Line width={row.sub} fontSize={12} />}
                    {row.contact && (
                        <div className="drilldown-row">
                            <Skeleton variant="circular" width={20} height={20} />
                            <Line width={emailWidths[index % emailWidths.length]} fontSize={12} />
                            <Skeleton variant="circular" width={20} height={20} />
                            <Line width={80} fontSize={12} />
                        </div>
                    )}
                </div>
            </div>
        )
    }
    if (row.split) {
        return (
            <div className="drilldown-split">
                <Line width={row.split[0]} fontSize={row.fontSize || 14} />
                <Line width={row.split[1]} fontSize={row.fontSize || 14} />
            </div>
        )
    }
    if (row.eligibility) {
        return (
            <div className="drilldown-row">
                <Line width={72} />
                <Skeleton variant="circular" width={24} height={24} />
                <Line width={170} />
            </div>
        )
    }
    if (row.buttons) {
        return (
            <div className="drilldown-row">
                {row.buttons.map((width, button) => (
                    <ButtonSkeleton key={button} width={width} />
                ))}
            </div>
        )
    }
    const widths = Array.isArray(row.text) ? row.text : [row.text]
    return <Line width={widths[index % widths.length]} fontSize={row.fontSize} />
}

export const DrilldownCardsSkeleton = ({rows, count = 4}) => (
    <SkeletonContainer>
        {Array.from({length: count}).map((_, index) => (
            <Fragment key={index}>
                <br />
                <div className="drilldown-card">
                    {rows.map((row, key) => (
                        <DrilldownRow key={key} row={row} index={index} />
                    ))}
                </div>
            </Fragment>
        ))}
    </SkeletonContainer>
)

// The HHAX Sync modals: service, client and caregiver, scheduled and reported times (and the rejection reason).
export const HhaxCardsSkeleton = ({count = 3, reason = false}) => (
    <SkeletonContainer>
        {Array.from({length: count}).map((_, index) => (
            <Fragment key={index}>
                <br />
                <div className="drilldown-card">
                    <Line width={serviceWidths[index % serviceWidths.length]} fontSize={16} />
                    <div className="hhax-people-row">
                        {[48, 72].map((labelWidth, person) => (
                            <div key={person}>
                                <Line width={labelWidth} fontSize={12} />
                                <div className="hhax-people-card">
                                    <AvatarSkeleton size={40} />
                                    <Line width={nameWidths[(index + person) % nameWidths.length]} fontSize={16} />
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="hhax-datetime-row">
                        {[80, 56].map((labelWidth, col) => (
                            <div className="hhax-datetime-col" key={col}>
                                <Line width={labelWidth} fontSize={12} />
                                <DatetimeSkeleton />
                            </div>
                        ))}
                    </div>
                    {reason && (
                        <>
                            <Line width={60} fontSize={12} />
                            <Line width="70%" fontSize={12} />
                        </>
                    )}
                </div>
            </Fragment>
        ))}
    </SkeletonContainer>
)
